import { getTokenCookie, clearTokenCookie } from "@/lib/auth/cookies";
import { getAuthMode, isAuthDisabled, redirectToLogin } from "@/lib/auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";
// Fallback used only for local dev against an AUTH_DISABLED backend.
const DEV_FALLBACK_TOKEN = import.meta.env.VITE_API_AUTH_TOKEN ?? "";

/**
 * The bearer token for `/api/*` calls. The real SSO token lives in the
 * shared `.signatureaviation.com` cookie; we fall back to the static dev token
 * only when no cookie is present (local AUTH_DISABLED mode).
 */
function resolveAuthToken(): string {
  return getTokenCookie() ?? DEV_FALLBACK_TOKEN;
}

/**
 * One-shot 401 re-handoff guard for skyagent mode. Prevents infinite redirect
 * loops when a freshly-minted token is still rejected. Reset on successful
 * auth callback so later legitimate expirations still re-mint.
 */
let handoffAttemptedThisLoad = false;

/** Called from processAuthCallback on successful callback — resets the one-shot guard. */
export function resetHandoffGuard(): void {
  handoffAttemptedThisLoad = false;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  token?: string;
  requestId?: string;
}

export interface ApiErrorInit {
  status: number;
  type?: string;
  title?: string;
  detail?: string;
  instance?: string;
  requestId?: string;
  validation?: unknown[];
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly type: string;
  public readonly title: string;
  public readonly detail?: string;
  public readonly instance?: string;
  public readonly requestId?: string;
  public readonly validation?: unknown[];

  constructor(init: ApiErrorInit) {
    const title = init.title ?? `Request failed with status ${init.status}`;
    super(title);
    this.name = "ApiError";
    this.status = init.status;
    this.type = init.type ?? `/errors/${init.status}`;
    this.title = title;
    this.detail = init.detail;
    this.instance = init.instance;
    this.requestId = init.requestId;
    this.validation = init.validation;
  }
}

export function panelErrorTitle(error: unknown): string {
  if (error instanceof ApiError) return error.title;
  if (error instanceof Error) return error.message;
  return "Request failed";
}

interface Envelope<T> {
  data: T;
  meta?: { requestId?: string; tookMs?: number };
}

function newRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function isApiV1Path(path: string): boolean {
  return path.startsWith("/api/");
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    method = "GET",
    body,
    headers = {},
    signal,
    token = resolveAuthToken(),
    requestId = newRequestId(),
  } = options;

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    "X-Request-Id": requestId,
    ...headers,
  };

  if (body !== undefined && finalHeaders["Content-Type"] === undefined) {
    finalHeaders["Content-Type"] = "application/json";
  }

  if (token && isApiV1Path(path) && finalHeaders.Authorization === undefined) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    signal,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get("content-type") ?? "";
  const isJson =
    contentType.includes("application/json") ||
    contentType.includes("application/problem+json");

  const payload = isJson
    ? await res.json().catch(() => undefined)
    : await res.text().catch(() => undefined);

  if (!res.ok) {
    // An expired / missing session on a protected call → clear it and send the
    // user to sign in. Exclude the auth endpoints themselves so a wrong-password
    // 401 surfaces in the login form instead of reloading the page.
    //
    // In skyagent mode, we re-initiate the handoff AT MOST ONCE per page-load
    // to prevent infinite loops (e.g. if the fresh token is still rejected).
    // A second 401 after a handoff surfaces the error to the caller instead.
    if (
      res.status === 401 &&
      !isAuthDisabled() &&
      isApiV1Path(path) &&
      !path.startsWith("/api/v1/auth/")
    ) {
      clearTokenCookie();
      if (getAuthMode() === "skyagent") {
        if (!handoffAttemptedThisLoad) {
          handoffAttemptedThisLoad = true;
          redirectToLogin();
        }
        // else: already attempted a handoff this load; surface the error.
      } else {
        redirectToLogin();
      }
    }
    throw parseRfc7807(res.status, payload, requestId);
  }

  if (
    payload !== null &&
    typeof payload === "object" &&
    "data" in (payload as Record<string, unknown>)
  ) {
    return (payload as Envelope<T>).data;
  }

  return payload as T;
}

function parseRfc7807(
  status: number,
  payload: unknown,
  requestId: string,
): ApiError {
  if (payload !== null && typeof payload === "object") {
    const p = payload as Record<string, unknown>;
    return new ApiError({
      status,
      type: typeof p.type === "string" ? p.type : undefined,
      title: typeof p.title === "string" ? p.title : undefined,
      detail: typeof p.detail === "string" ? p.detail : undefined,
      instance: typeof p.instance === "string" ? p.instance : undefined,
      requestId:
        typeof p.requestId === "string" ? p.requestId : requestId,
      validation: Array.isArray(p.validation) ? p.validation : undefined,
    });
  }

  return new ApiError({
    status,
    requestId,
    detail: typeof payload === "string" && payload.length > 0 ? payload : undefined,
  });
}

/**
 * Cross-origin nav feed from SkyAgent (the only non-Ops-API fetch in the app).
 *
 * The Performance Dashboard renders the SkyAgent left-hand menu so users keep
 * their bearings. SkyAgent owns the role-gated nav, so we fetch the resolved
 * list from its `GET /api/nav`, authenticated with the same SSO bearer token.
 * It lives here to honour the "all fetch in api-client" rule, but deliberately
 * bypasses `request()` — that path prepends the Ops-API base URL and only
 * authorises same-origin `/api/*` calls, neither of which fits a cross-origin
 * SkyAgent request. Caller validates the shape (see useSkyAgentNav).
 */
export async function fetchSkyAgentNav(
  skyagentUrl: string,
  token: string,
  signal?: AbortSignal,
): Promise<unknown> {
  const url = `${skyagentUrl.replace(/\/$/, "")}/api/nav`;
  const res = await fetch(url, {
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) {
    throw new ApiError({ status: res.status, title: `SkyAgent nav request failed (${res.status})` });
  }
  return res.json();
}

type ReadOptions = Omit<RequestOptions, "method" | "body">;
type WriteOptions = Omit<RequestOptions, "method" | "body">;

export const api = {
  get: <T>(path: string, options?: ReadOptions) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: WriteOptions) =>
    request<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options?: WriteOptions) =>
    request<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, options?: WriteOptions) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  delete: <T = void>(path: string, options?: ReadOptions) =>
    request<T>(path, { ...options, method: "DELETE" }),
};
